import React, { createContext, useContext, useState, useEffect } from 'react';

const GlobalContext = createContext();

const eventBus = {
  listeners: {},
  on(event, handler) {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(handler);
  },
  off(event, handler) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter(h => h !== handler);
  },
  emit(event, data) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach(handler => handler(data));
  }
};

export const GlobalProvider = ({ children }) => {
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  // null = loading/unknown, true = premium, false = not premium
  const [hasActiveSubscription, setHasActiveSubscription] = useState(null);

  // Capture userId and projectId from URL parameters on mount and keep checking
  useEffect(() => {
    const captureParams = () => {
      const params = new URLSearchParams(window.location.search);
      const userId = params.get('userId');
      const projectId = params.get('projectId');
      
      console.log('[GlobalContext] Capturing params:', { userId, projectId, url: window.location.href });
      
      // ALWAYS prioritize URL parameter (for Adalo WebView)
      if (userId) {
        console.log('[GlobalContext] Setting userId from URL:', userId);
        setCurrentUserId(userId);
        // Try to store in sessionStorage (works better in iframes than localStorage)
        try {
          sessionStorage.setItem('buildpilot_userId', userId);
        } catch (e) {
          console.warn('SessionStorage not available');
        }
      } else {
        // Fallback to sessionStorage if no URL param
        try {
          const storedUserId = sessionStorage.getItem('buildpilot_userId');
          if (storedUserId) {
            console.log('[GlobalContext] Using stored userId:', storedUserId);
            setCurrentUserId(storedUserId);
          } else {
            // Fallback to preview/test userId for standalone testing (581 is the designated test user)
            console.log('[GlobalContext] Using fallback preview userId: 581');
            setCurrentUserId('581');
            sessionStorage.setItem('buildpilot_userId', '581');
          }
        } catch (e) {
          console.warn('SessionStorage not available');
          // Final fallback (581 is the designated test user)
          setCurrentUserId('581');
        }
      }

      // Same for projectId
      if (projectId) {
        setCurrentProjectId(projectId);
        try {
          sessionStorage.setItem('buildpilot_projectId', projectId);
        } catch (e) {
          console.warn('SessionStorage not available');
        }
      } else {
        try {
          const storedProjectId = sessionStorage.getItem('buildpilot_projectId');
          if (storedProjectId) {
            setCurrentProjectId(storedProjectId);
          }
        } catch (e) {
          console.warn('SessionStorage not available');
        }
      }
    };

    // Capture on mount
    captureParams();

    // Also listen for URL changes (in case of navigation within WebView)
    window.addEventListener('popstate', captureParams);
    
    return () => {
      window.removeEventListener('popstate', captureParams);
    };
  }, []);


  // Mock project - always available, no database needed
  const mockProject = {
    id: currentProjectId || 'demo-project-123',
    name: 'My Dream Home',
    address: '123 BuildPilot Lane, Sydney NSW',
    state: 'NSW',
    projectType: 'Single Storey'
  };

  // Check subscription status when userId changes
  useEffect(() => {
    if (currentUserId) {
      const checkSubscription = async () => {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
          console.log('[GlobalContext] Checking subscription for userId:', currentUserId);
          const response = await fetch(`${backendUrl}/api/check-subscription?userId=${currentUserId}`);
          const data = await response.json();
          console.log('[GlobalContext] Subscription response:', data);
          if (data.success) {
            setHasActiveSubscription(data.hasActiveSubscription === true);
          } else {
            // If API returns success: false, default to showing premium (better UX)
            console.warn('[GlobalContext] Subscription check returned success: false, defaulting to premium');
            setHasActiveSubscription(true);
          }
        } catch (error) {
          console.error('[GlobalContext] Error checking subscription:', error);
          // On error, default to showing premium tools (better UX than hiding them)
          setHasActiveSubscription(true);
        }
      };
      checkSubscription();
    } else {
      // No userId - default to showing all tools
      console.log('[GlobalContext] No userId, defaulting to show all tools');
      setHasActiveSubscription(true);
    }
  }, [currentUserId]);

  const value = {
    currentUserId,
    currentProjectId: currentProjectId || mockProject.id,
    currentProject: mockProject,
    projects: [mockProject],
    loading: false,
    eventBus,
    hasActiveSubscription
  };

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};

export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobal must be used within GlobalProvider');
  }
  return context;
};